/**
 * eSign — "Captured details" panel for an authority record.
 *
 * Christian (ticket 12525266778, 3 Aug): the vendor/deal info that is captured but
 * NOT printed on the fixed legal authority — DOB, postal address, banking,
 * conveyancer, body corporate — lives on a separate page connected to the e-sign
 * record. These are exactly the schema questions flagged `capture_only`, so we read
 * the authority form (schema + values) and render only those, grouped by section.
 * Staff can edit them here; saving writes back through the existing form-values API
 * (DOB + postal are also mirrored onto the client's Contact profile server-side).
 */
import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { Pencil, Save, X } from 'lucide-react';
import esignApi from '../../../api/esign';

const inputBase =
  'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500/40';

// Stored as an ISO date (YYYY-MM-DD from the date input); show DD / MM / YYYY read-only.
const fmtDate = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(v || '');
  return m ? `${m[3]} / ${m[2]} / ${m[1]}` : v || '';
};

const inputType = (t) => (t === 'date' ? 'date' : t === 'email' ? 'email' : t === 'tel' ? 'tel' : 'text');

export default function CapturedDetailsPanel({ envelopeId, canEdit }) {
  const [schema, setSchema] = useState(null);
  const [values, setValues] = useState({});
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await esignApi.getAuthorityForm(envelopeId);
      setSchema(data.schema || null);
      setValues(data.values || {});
    } catch { toast.error('Could not load captured details.'); }
    finally { setLoading(false); }
  }, [envelopeId]);

  useEffect(() => { load(); }, [load]);

  // Only the capture-only questions (never stamped on the authority), grouped by section.
  const groups = (schema?.sections || [])
    .map((s) => ({ key: s.key, title: s.title, questions: (s.questions || []).filter((q) => q.capture_only) }))
    .filter((g) => g.questions.length);

  const startEdit = () => { setDraft({ ...values }); setEditing(true); };
  const cancel = () => { setEditing(false); setDraft({}); };
  const save = async () => {
    // Send only the capture-only keys this panel manages.
    const payload = {};
    groups.forEach((g) => g.questions.forEach((q) => {
      if (draft[q.key] !== undefined) payload[q.key] = draft[q.key];
    }));
    setSaving(true);
    try {
      await esignApi.saveAuthorityForm(envelopeId, payload);
      setValues((v) => ({ ...v, ...payload }));
      setEditing(false);
      toast.success('Captured details saved.');
    } catch (e) { toast.error(e?.response?.data?.error || 'Save failed.'); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="bg-white border rounded-xl p-6 text-gray-400">Loading…</div>;
  if (!groups.length) {
    return <div className="bg-white border rounded-xl p-6 text-gray-400">No captured details for this document.</div>;
  }

  const hasAny = groups.some((g) => g.questions.some((q) => (values[q.key] ?? '').toString().trim()));

  return (
    <div className="bg-white border rounded-xl p-5 sm:p-6">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-semibold text-gray-900">Captured details</h2>
          <p className="text-xs text-gray-400 mt-0.5">
            Held against this e-sign record for the sale. Not printed on the signed authority.
          </p>
        </div>
        {canEdit && !editing && (
          <button onClick={startEdit} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50 shrink-0">
            <Pencil className="w-3.5 h-3.5" /> Edit
          </button>
        )}
        {editing && (
          <div className="flex items-center gap-2 shrink-0">
            <button onClick={cancel} disabled={saving} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-50">
              <X className="w-3.5 h-3.5" /> Cancel
            </button>
            <button onClick={save} disabled={saving} className="flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}
      </div>

      {!editing && !hasAny && (
        <p className="text-sm text-gray-400 mt-4">
          Nothing captured yet.{canEdit ? ' Use Edit to add these details.' : ''}
        </p>
      )}

      <div className="mt-5 space-y-6">
        {groups.map((g) => (
          <section key={g.key}>
            <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">{g.title}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {g.questions.map((q) => {
                if (editing) {
                  return (
                    <label key={q.key} className="block">
                      <span className="block text-sm font-medium text-gray-700 mb-1">{q.label}</span>
                      <input
                        type={inputType(q.type)}
                        value={draft[q.key] ?? ''}
                        onChange={(e) => setDraft((d) => ({ ...d, [q.key]: e.target.value }))}
                        className={inputBase}
                      />
                      {q.help && <span className="block text-xs text-gray-400 mt-1">{q.help}</span>}
                    </label>
                  );
                }
                const raw = values[q.key] ?? '';
                const shown = q.type === 'date' ? fmtDate(raw) : raw;
                return (
                  <div key={q.key} className="min-w-0">
                    <div className="text-xs text-gray-500">{q.label}</div>
                    <div className="text-sm text-gray-900 break-words">
                      {shown ? shown : <span className="text-gray-300">—</span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
